import { useId, type ReactNode } from "react";
import { cx } from "../../components/ui.js";

/**
 * Os controles do inspetor da camada. Todos com alvo de toque de 44 px e rótulo visível: o operador está de pé,
 * com o celular numa mão, num salão mal iluminado.
 */

/**
 * Um controle deslizante com o valor ao lado do rótulo. `aoSoltar` fecha o agrupamento do histórico: arrastar o
 * controle é **uma** entrada do desfazer, e não uma por valor passado.
 */
export function Deslizante({
  rotulo,
  valor,
  min,
  max,
  passo,
  formato = (v) => String(v),
  aoMudar,
  aoSoltar,
  desabilitado,
}: {
  rotulo: string;
  valor: number;
  min: number;
  max: number;
  passo: number;
  formato?: (valor: number) => string;
  aoMudar: (valor: number) => void;
  aoSoltar: () => void;
  desabilitado?: boolean;
}) {
  const id = useId();
  return (
    <div className={cx(desabilitado && "opacity-50")}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-[0.8125rem] font-medium text-texto-suave">
          {rotulo}
        </label>
        <output htmlFor={id} className="text-[0.8125rem] tabular-nums text-texto">
          {formato(valor)}
        </output>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={passo}
        value={Math.min(Math.max(valor, min), max)}
        disabled={desabilitado}
        onChange={(e) => aoMudar(Number(e.target.value))}
        onPointerUp={aoSoltar}
        onKeyUp={aoSoltar}
        onBlur={aoSoltar}
        className="block h-11 w-full accent-[var(--color-brasa)]"
      />
    </div>
  );
}

/** As cores de partida: as que leem bem sobre uma foto, e as do tema. */
export const AMOSTRAS_DE_COR: readonly { cor: string; nome: string }[] = [
  { cor: "#ffffff", nome: "Branco" },
  { cor: "#000000", nome: "Preto" },
  { cor: "#d9b35f", nome: "Dourado" },
  { cor: "#f0a04b", nome: "Âmbar" },
  { cor: "#d64545", nome: "Vermelho" },
  { cor: "#2b6cb0", nome: "Azul" },
  { cor: "#15563f", nome: "Verde" },
];

/**
 * Escolha de cor: as amostras e um seletor livre. A cor é sempre `#rrggbb` (o formato que o servidor aceita).
 */
export function SeletorDeCor({
  rotulo,
  valor,
  aoMudar,
  aoSoltar,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (cor: string) => void;
  aoSoltar: () => void;
}) {
  const id = useId();
  return (
    <div role="group" aria-label={rotulo}>
      <span className="text-[0.8125rem] font-medium text-texto-suave">{rotulo}</span>
      <div className="mt-1 flex flex-wrap items-center">
        {AMOSTRAS_DE_COR.map((a) => {
          const atual = a.cor.toLowerCase() === valor.toLowerCase();
          return (
            <button
              key={a.cor}
              type="button"
              aria-label={a.nome}
              aria-pressed={atual}
              onClick={() => {
                aoMudar(a.cor);
                aoSoltar();
              }}
              // O botão tem 44 px (o alvo do dedo); a bolinha, 28.
              className="inline-flex size-11 items-center justify-center"
            >
              <span
                className={cx(
                  "size-7 rounded-full border",
                  atual ? "border-brasa ring-2 ring-brasa" : "border-borda-forte",
                )}
                style={{ backgroundColor: a.cor }}
              />
            </button>
          );
        })}
        <label
          htmlFor={id}
          className="ml-1 inline-flex min-h-11 items-center gap-2 text-[0.8125rem] text-texto-suave"
        >
          <input
            id={id}
            type="color"
            value={valor}
            onChange={(e) => aoMudar(e.target.value)}
            onBlur={aoSoltar}
            className="h-9 w-11 cursor-pointer rounded-peca border border-borda bg-superficie p-0.5"
          />
          Outra cor
        </label>
      </div>
    </div>
  );
}

/** Um bloco do inspetor, com título. */
export function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[0.9375rem] font-medium text-texto">{titulo}</h3>
      {children}
    </section>
  );
}
